/**
 * Open Graph Image Configuration
 * 
 * Centralized mapping of OG images for all page types.
 * When adding new pages, add the corresponding OG image mapping here.
 * 
 * Image Requirements:
 * - Dimensions: 1200 x 630 pixels
 * - Format: JPG (preferred) or PNG
 * - Max size: 300KB
 * - Location: /public/og/ directory
 */

const BASE_URL = 'https://events.jigsawconferences.co.uk';

// Default fallback image
export const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.jpg`;

// Page-specific OG images
export const OG_IMAGES = {
  // Main pages
  home: `${BASE_URL}/og/og-home.jpg`,
  events: `${BASE_URL}/og/og-events.jpg`,
  blog: `${BASE_URL}/og/og-blog.jpg`,
  insights: `${BASE_URL}/og/og-insights.jpg`,
  subscribe: `${BASE_URL}/og/og-subscribe.jpg`,
  compliance: `${BASE_URL}/og/og-compliance.jpg`,
  
  // Feature pages
  boost: `${BASE_URL}/og/og-boost.jpg`,
  genome: `${BASE_URL}/og/og-genome.jpg`,
  trends: `${BASE_URL}/og/og-trends.jpg`,
  compare: `${BASE_URL}/og/og-compare.jpg`,
  
  // Listing pages
  listYourEvent: `${BASE_URL}/og/og-list-event.jpg`,
  embedDemo: `${BASE_URL}/og/og-embed.jpg`,
};

// City-specific OG images
export const CITY_OG_IMAGES = {
  london: `${BASE_URL}/og/cities/og-london.jpg`,
  manchester: `${BASE_URL}/og/cities/og-manchester.jpg`,
  birmingham: `${BASE_URL}/og/cities/og-birmingham.jpg`,
  edinburgh: `${BASE_URL}/og/cities/og-edinburgh.jpg`,
  glasgow: `${BASE_URL}/og/cities/og-glasgow.jpg`,
  leeds: `${BASE_URL}/og/cities/og-leeds.jpg`,
  liverpool: `${BASE_URL}/og/cities/og-liverpool.jpg`,
  bristol: `${BASE_URL}/og/cities/og-bristol.jpg`,
  // Add more cities as needed
};

// Venue-specific OG images
export const VENUE_OG_IMAGES = {
  'excel-london': `${BASE_URL}/og/venues/og-excel-london.jpg`,
  'olympia-london': `${BASE_URL}/og/venues/og-olympia-london.jpg`,
  'nec-birmingham': `${BASE_URL}/og/venues/og-nec-birmingham.jpg`,
  'manchester-central': `${BASE_URL}/og/venues/og-manchester-central.jpg`,
  // Add more venues as needed
};

// Sector-specific OG images
export const SECTOR_OG_IMAGES = {
  technology: `${BASE_URL}/og/sectors/og-technology.jpg`,
  healthcare: `${BASE_URL}/og/sectors/og-healthcare.jpg`,
  finance: `${BASE_URL}/og/sectors/og-finance.jpg`,
  retail: `${BASE_URL}/og/sectors/og-retail.jpg`,
  manufacturing: `${BASE_URL}/og/sectors/og-manufacturing.jpg`,
  energy: `${BASE_URL}/og/sectors/og-energy.jpg`,
  // Add more sectors as needed
};

/**
 * Get OG image for a specific page type
 * @param {string} pageType - Type of page (home, events, blog, etc.)
 * @param {string} slug - Optional slug for dynamic pages (city, venue, sector)
 * @returns {string} OG image URL
 */
export function getOgImage(pageType, slug = null) {
  // Check for specific page type
  if (OG_IMAGES[pageType]) {
    return OG_IMAGES[pageType];
  }
  
  // Check for city-specific image
  if (pageType === 'city' && slug && CITY_OG_IMAGES[slug]) {
    return CITY_OG_IMAGES[slug];
  }
  
  // Check for venue-specific image
  if (pageType === 'venue' && slug && VENUE_OG_IMAGES[slug]) {
    return VENUE_OG_IMAGES[slug];
  }
  
  // Check for sector-specific image
  if (pageType === 'sector' && slug && SECTOR_OG_IMAGES[slug]) {
    return SECTOR_OG_IMAGES[slug];
  }
  
  // Return default fallback
  return DEFAULT_OG_IMAGE;
}

/**
 * Get OG image for event detail page
 * For dynamic event images, you can implement an API endpoint
 * @param {object} event - Event object with id, slug, imageUrl
 * @returns {string} OG image URL
 */
export function getEventOgImage(event) {
  // Option 1: Use event's own image if available
  if (event?.imageUrl) {
    return event.imageUrl;
  }
  
  // Option 2: Use dynamic OG image API (if implemented)
  // return `${BASE_URL}/api/og-image?type=event&id=${event.id}`;
  
  // Option 3: Use city-specific fallback
  if (event?.citySlug && CITY_OG_IMAGES[event.citySlug]) {
    return CITY_OG_IMAGES[event.citySlug];
  }
  
  // Default fallback
  return OG_IMAGES.events || DEFAULT_OG_IMAGE;
}

/**
 * Get OG image for blog post
 * @param {object} post - Blog post object with slug, featuredImage
 * @returns {string} OG image URL
 */
export function getBlogOgImage(post) {
  // Use post's featured image if available
  if (post?.featuredImage) {
    return post.featuredImage;
  }
  
  // Default blog fallback
  return OG_IMAGES.blog || DEFAULT_OG_IMAGE;
}

/**
 * Get OG image for insight report
 * @param {object} report - Report object with slug, coverImage
 * @returns {string} OG image URL
 */
export function getInsightOgImage(report) {
  if (report?.coverImage) {
    return report.coverImage;
  }
  return OG_IMAGES.insights || DEFAULT_OG_IMAGE;
}

// Export all for convenience
export default {
  DEFAULT_OG_IMAGE,
  OG_IMAGES,
  CITY_OG_IMAGES,
  VENUE_OG_IMAGES,
  SECTOR_OG_IMAGES,
  getOgImage,
  getEventOgImage,
  getBlogOgImage,
  getInsightOgImage,
};
